import React, { useState, useEffect } from 'react';
import './AdminEvents.css'; // Reusing event CSS for layout symmetry
import { supabase } from '../../config/supabase';
import { useRealtimeJobs } from '../../hooks/useRealtime';
import Modal from './Modal';

const AdminJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        company: '',
        location: '',
        category: 'AI/ML in R&D', // Default category
        description: '',
        apply_url: ''
    });
    
    const [editingId, setEditingId] = useState(null);

    // Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: 'confirm', 
        title: '',
        message: '',
        onConfirm: null
    });

    const jobCategories = [
        'AI/ML in R&D',
        'GxP/Data Integrity',
        'Pharma 4.0 Digital',
        'Global Regulatory Affairs'
    ];

    // Real-time: Refresh when jobs change
    useRealtimeJobs(() => {
        console.log('Jobs updated in real-time');
        fetchJobs();
    });

    useEffect(() => {
        fetchJobs();
    }, []);

    // Fetch directly from Supabase
    const fetchJobs = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('jobs')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            
            setJobs(data || []);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (error) {
            console.error('Failed to fetch jobs:', error);
            setJobs([]);
        }
        setIsLoading(false);
    };

    const closeModal = () => {
        setModalConfig({ ...modalConfig, isOpen: false });
    };

    const showModal = (type, title, message, onConfirm = null) => {
        setModalConfig({ isOpen: true, type, title, message, onConfirm });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editingId) {
            try {
                const { error } = await supabase
                    .from('jobs')
                    .update(formData)
                    .eq('id', editingId);
                
                if (error) throw error;
                
                showModal('success', 'Success!', 'Job updated successfully.');
                setEditingId(null);
                fetchJobs();
            } catch (error) {
                showModal('error', 'Update Failed', error.message);
            }
        } else {
            try {
                const { error } = await supabase
                    .from('jobs')
                    .insert([formData]);
                
                if (error) throw error;
                
                showModal('success', 'Success!', 'New job posted successfully.');
                fetchJobs();
            } catch (error) {
                showModal('error', 'Add Failed', error.message);
            }
        }

        setFormData({ title: '', company: '', location: '', category: 'AI/ML in R&D', description: '', apply_url: '' });
    };

    const handleEdit = (job) => {
        setFormData({
            title: job.title || '',
            company: job.company || '',
            location: job.location || '',
            category: job.category || 'AI/ML in R&D',
            description: job.description || '',
            apply_url: job.apply_url || ''
        });
        setEditingId(job.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const confirmDelete = (id) => {
        showModal(
            'confirm',
            'Delete Job?',
            'Are you sure you want to delete this job posting? This action cannot be undone.',
            async () => {
                try {
                    const { error } = await supabase
                        .from('jobs')
                        .delete()
                        .eq('id', id);
                    
                    if (error) throw error;
                    
                    closeModal();
                    fetchJobs();
                } catch (error) {
                    closeModal();
                    setTimeout(() => showModal('error', 'Error', error.message), 300);
                }
            }
        );
    };

    return (
        <div className="admin-events-container">
            <Modal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={closeModal}
            />

            <h2>Manage Real-Time Jobs</h2>

            <div className="event-form-card">
                <h3>{editingId ? 'Edit Job Posting' : 'Post New Job'}</h3>
                <form onSubmit={handleSubmit} className="event-form">
                    <input type="text" name="title" placeholder="Job Title" value={formData.title} onChange={handleChange} required />
                    <input type="text" name="company" placeholder="Company Name" value={formData.company} onChange={handleChange} required />
                    <input type="text" name="location" placeholder="Location (e.g., Remote, UK)" value={formData.location} onChange={handleChange} required />
                    
                    <select name="category" value={formData.category} onChange={handleChange} required style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', margin: '5px 0' }}>
                        {jobCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>

                    <input type="url" name="apply_url" placeholder="Application URL Link" value={formData.apply_url} onChange={handleChange} required />
                    <textarea name="description" placeholder="Short description..." value={formData.description} onChange={handleChange} required rows={3} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', margin: '5px 0' }} />
                    
                    <button type="submit" className="save-btn">{editingId ? 'Update Job' : 'Post Job'}</button>
                    {editingId && (
                        <button type="button" className="cancel-btn" onClick={() => {
                            setEditingId(null);
                            setFormData({ title: '', company: '', location: '', category: 'AI/ML in R&D', description: '', apply_url: '' });
                        }}>Cancel</button>
                    )}
                </form>
            </div>

            <div className="events-list">
                <h3>Current Job Postings</h3>
                {isLoading ? <p>Loading jobs...</p> : (
                    <table>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Title</th>
                                <th>Company</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => (
                                <tr key={job.id}>
                                    <td><span style={{background: '#e2e8f0', padding: '4px 8px', borderRadius: '12px', fontSize: '0.85rem'}}>{job.category}</span></td>
                                    <td>{job.title}</td>
                                    <td>{job.company}</td>
                                    <td>
                                        <button onClick={() => handleEdit(job)} className="edit-btn">Edit</button>
                                        <button onClick={() => confirmDelete(job.id)} className="delete-btn">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminJobs;
